/**
 * EditUserDialog — Edit an existing user's identity, employment, and status.
 *
 * Used from the user detail page header. W1a-04 wires this dialog to
 * the live PATCH /api/v1/users/:id endpoint + (when identity fields are
 * dirty) the F61 PATCH /api/v1/users/:id/identity-fields endpoint.
 *
 * Field routing:
 *   Profile fields  (firstName, lastName, phone, avatar, role)
 *      → useUpdateUser()           → PATCH /api/v1/users/:id
 *   Identity fields (clearance, US-Person, HIPAA BAA)
 *      → useUpdateIdentityFields() → PATCH /api/v1/users/:id/identity-fields  (F61)
 *
 * If both kinds of fields are dirty the calls run sequentially: profile
 * first, then identity. If profile succeeds but identity fails, surface
 * a "partial save" toast.
 *
 * NOT covered here (separate flows):
 *   - Lifecycle (suspend/lock/etc) → header buttons via useUpdateUserStatus()
 *   - Roles list (multi-role)      → Assign Role dialog
 *   - Groups                        → Add to Group dialog
 *   - MFA / password               → per-user action buttons
 *   - US Person verification flow  → US Person Verification dialog
 *
 * Field-level RBAC:
 *   - Role selector  : disabled if !useHasPermission('role:assign', 'users')
 *                       OR viewing own profile (separation-of-duties).
 *   - Email          : disabled when viewing own profile (security
 *                       hardening; admin can change *another* user's email).
 *   - Identity fields: gated by useIdentityFieldPolicy().isVisible(key)
 *                       (Phase B Step 2.5 framework gate).
 */

import { useState, useEffect, useRef } from 'react';
import {
  Pencil, Save, User as UserIcon, Briefcase, Building2,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  type User, type EmploymentType,
} from '@/lib/mock-users-data';
// The same badge the roster and the detail header render, so one account
// state has one appearance everywhere it is shown.
import { UserStatusBadge } from '@/components/users/user-badges';
import {
  useUpdateUser,
  useUpdateIdentityFields,
  useAllDepartments,
  useUsers,
  type UpdateUserBody,
  type UpdateIdentityFieldsBody,
} from '@/lib/use-users';
// B2b-T3 — the ONE departments control, shared with the create wizard.
import {
  DepartmentScopePicker,
  isCompleteScope,
  sameScope,
  scopeFromUserDepartments,
  type DepartmentScopeValue,
} from '@/components/users/department-scope-picker';
// B2c-T2 — the employment-type options come from the server, not from four
// SelectItems in this file. See `use-user-field-options.ts`.
import { useUserFieldOptions } from '@/lib/use-user-field-options';
import { FieldOptionsNotice } from './field-options-notice';
import { getApiErrorCode, getApiErrorMessage } from '@/lib/api-error';
import { useHasPermission } from '@/lib/use-has-permission';
import { useAuth } from '@/contexts/auth-context';
import { normalizeDateOnly, isDateOnlyDirty } from '@/lib/format';
import { DateInput } from './date-input';

export interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onSaved: (user: User) => void;
}

/**
 * Map a role identifier (in any of the three shapes the codebase
 * has historically used) to the API's role enum.
 *
 * Accepted shapes:
 *   - bare role KEYS (`admin`, `member`, `viewer`, `owner`, `power_user`) —
 *     what `GET /users/roles` carries and what the adapter emits since B1-T4
 *   - `system:admin` … (the sentinel the API used to synthesise; accepted so
 *     older form state still resolves, emitted by nothing)
 *   - `role-admin`, `role-member`, `role-viewer`, `role-power_user`
 *     (legacy mock-catalog shape; kept for backward compat with
 *     hardcoded UI defaults)
 *   - bare `admin`, `member`, `viewer`, `owner`
 *
 * Anything that doesn't match a known suffix snaps to `member`.
 * `owner` snaps to `admin` because the API's `role` body enum is
 * limited to `viewer | member | admin` (owner promotion is a
 * separate flow). `power_user` snaps to `member` (v1 has no
 * power_user — see W1a-27 §reconciliation).
 */
function snapRoleForApi(roleId: string): 'viewer' | 'member' | 'admin' {
  const stripped = roleId
    .replace(/^role-/, '')
    .replace(/^system:/, '');
  if (stripped === 'admin' || stripped === 'owner') return 'admin';
  if (stripped === 'viewer') return 'viewer';
  return 'member';
}

interface ServerError {
  status?: number;
  data?: { error?: { code?: string; message?: string; details?: unknown } };
  message?: string;
}

function parseErr(err: unknown): { code: string; message: string } {
  // For VALIDATION_ERROR, getApiErrorMessage returns the api-client's
  // enriched message (with field-level detail) rather than the raw
  // "Validation failed" — see PR #327 / api-error.ts.
  const code = getApiErrorCode(err);
  const msg = getApiErrorMessage(err, 'Failed to update user');
  return { code, message: msg };
}

export function EditUserDialog({ open, onOpenChange, user, onSaved }: EditUserDialogProps) {
  const { user: authUser } = useAuth();
  const canAssignRole = useHasPermission('role:assign', 'users');
  const updateUser = useUpdateUser();
  const updateIdentity = useUpdateIdentityFields();
  // B2c-T2 — the employment-type options and the one rule that varies per
  // option (does this engagement have a scheduled end?), from the server.
  //
  // Reviews B2cT2-BF-03 / BF-08 — the request's state is READ. Two things go
  // wrong silently otherwise: the Select renders a BLANK employment type for a
  // person who has one (the stored value matches no SelectItem), and
  // `requiresEndDate` answers `false` for an absent answer, so an existing
  // contractor's End date re-labels itself "(optional)" and loses the amber
  // "Required for a time-bounded engagement" warning.
  const {
    options: fieldOptions,
    isLoading: fieldOptionsLoading,
    error: fieldOptionsError,
    refetch: refetchFieldOptions,
  } = useUserFieldOptions();

  // Profile fields ─────────────────────────────────────────────────────
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<string>('member');
  // Mock-shape fields — preserved through the dialog but not sent to the
  // API (the v1 users table doesn't store them yet). Captured as F-W1a-04.3.
  const [position, setPosition] = useState('');
  // B2b-T3 — the SET, not one id. `{ all: false, ids: [] }` is the "nothing
  // loaded yet" value and is also the one scope Save refuses, which is why the
  // loader below always overwrites it from the server's answer.
  const [departmentScope, setDepartmentScope] = useState<DepartmentScopeValue>({
    all: false,
    ids: [],
  });
  const [managerId, setManagerId] = useState('');
  // B2c-T2 — null until the person's own value arrives, and nullable
  // thereafter: the column allows NULL and the dialog must not assert an
  // employment type the store does not hold. It defaulted to `'fte'`.
  const [employmentType, setEmploymentType] = useState<EmploymentType | null>(null);
  // Closes #216: Start Date is now editable. The mock-shape adapter
  // falls back to `createdAt` when the API has no startDate set, so we
  // normalise the loaded value to a YYYY-MM-DD slice for both
  // initialization and dirty detection.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Refs over the date inputs let us read the live DOM value at the
  // moment of click → handleSave, even when React has not yet flushed
  // a pending blur-driven setStartDate/setEndDate. Without this, the
  // sequence "type date → click Save" can race: blur fires first and
  // schedules a state update, the click handler runs synchronously and
  // reads the OLD state, so the body builder + dirty check both miss
  // the operator's typed value. Reading from the ref closes that race.
  // Closes #250 round 8.
  const startDateRef = useRef<HTMLInputElement | null>(null);
  const endDateRef = useRef<HTMLInputElement | null>(null);
  const [costCenter, setCostCenter] = useState('');

  // Field-level error surfacing.
  const [emailError, setEmailError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<{ code: string; message: string } | null>(null);

  // Closes #250 (round 7): the dependency list is `[open, user?.id]`,
  // NOT `[open, user]`. The useUser() hook produces a fresh `User`
  // object reference on every parent re-render (the adapter calls
  // `adaptApiUserToUser(data)` which constructs a new object each
  // time), so any background TanStack Query refetch (focus, mount,
  // periodic) flipped the `user` identity and re-fired this effect,
  // which RESET every form field — including the operator's typed
  // Start/End date — back to the loaded values, leaving Save
  // disabled. Round 6 fixed Start Date storage, but the dialog still
  // ate the typed values via this reset path. Keying the effect on
  // `user.id` makes it run exactly once per dialog-open/user pair.
  useEffect(() => {
    if (!open || !user) return;
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setPhoneNumber(user.phoneNumber ?? '');
    setEmail(user.email);
    setRoleId(user.roles[0]?.roleId ?? 'member');
    setPosition(user.position);
    setDepartmentScope(scopeFromUserDepartments(user.departments));
    setManagerId(user.managerId ?? 'none');
    setEmploymentType(user.employmentType);
    // Normalise via the canonical helper so a stray ISO timestamp
    // can't masquerade as a YYYY-MM-DD value. Mock data emits full
    // ISO timestamps; the API emits YYYY-MM-DD or null. Both are
    // handled by `normalizeDateOnly`, which never round-trips through
    // `new Date(...).toISOString()` (the source of the original tz
    // shift class of bugs — #182, #250 round 5).
    setStartDate(normalizeDateOnly(user.startDate) ?? '');
    setEndDate(normalizeDateOnly(user.endDate) ?? '');
    setCostCenter(user.costCenter ?? '');
    setEmailError(null);
    setServerError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id]);

  if (!user) return null;

  const isSelf = authUser.id === user.id;
  const isPending = updateUser.isPending || updateIdentity.isPending;

  // Field-level RBAC.
  // Role: disabled if user can't assign roles OR editing own profile.
  const roleDisabled = !canAssignRole || isSelf;
  // Email is the login identifier and is immutable here for everyone.
  // Changing the login identifier requires a dedicated identity-change
  // flow (with re-verification) — not a generic profile edit.
  const emailDisabled = true;

  const canSave =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0 &&
    // B2b-T3 — "not all departments, and none picked" is a scope that shows
    // nothing. The server refuses it with a 400; refusing it here means the
    // admin reads the reason under the control instead of in a toast.
    isCompleteScope(departmentScope);

  // Detect dirty fields so we know which API endpoints to call.
  const profileDirty =
    firstName !== user.firstName ||
    lastName !== user.lastName ||
    phoneNumber !== (user.phoneNumber ?? '') ||
    email !== user.email ||
    snapRoleForApi(roleId) !== snapRoleForApi(user.roles[0]?.roleId ?? 'member');

  // Identity-field changes require US Person verification flow / clearance
  // workflow which v1 doesn't surface from this dialog — leave for a
  // dedicated dialog. The Edit dialog routes only profile fields today.
  // (Identity edits are still available via the existing F61 flow on the
  // user detail page; we wire the mutation here so future-stage adds are
  // a one-line change.)
  const identityDirty = false;

  // ── Employment fields (bA1 — persisted via PATCH /users/:id) ──────────
  //
  // B2c-T2 — `uiToApiEmploymentType` is DELETED. It translated the browser's
  // four options into the store's four and was not injective: "Vendor" was
  // saved as `contractor`, "Partner" as `intern`, so the value the admin
  // picked was not the value the system held and the dialog redrew a
  // different answer on reopen. c32 made the two alphabets one list of six,
  // GET /users/field-options serves it, and the select's value IS the stored
  // value.

  // Has the user changed any employment field? Drives whether we add
  // those keys to the PATCH body. Date comparisons go through the
  // shared `isDateOnlyDirty` helper so the form's empty-string
  // sentinel ('') and the API's null sentinel collapse to the same
  // canonical "unset" value before the comparison, and any shape
  // drift (Date object, ISO timestamp) is normalised on both sides.
  //
  // Closes #250 round 8: read the live DOM value via ref (when the
  // input is mounted) so the dirty flag reflects the actual displayed
  // value even when React's controlled state has not yet caught up.
  // The DateInput's onBlur defence keeps state in sync most of the
  // time, but the ref is the canonical fallback when the operator
  // clicks Save while the date input still has focus and React has
  // not flushed the pending update yet.
  const liveStartDate = startDateRef.current?.value ?? startDate;
  const liveEndDate = endDateRef.current?.value ?? endDate;
  const employmentDirty =
    position !== user.position ||
    !sameScope(departmentScope, scopeFromUserDepartments(user.departments)) ||
    managerId !== (user.managerId ?? 'none') ||
    employmentType !== user.employmentType ||
    isDateOnlyDirty(user.startDate, liveStartDate) ||
    isDateOnlyDirty(user.endDate, liveEndDate) ||
    costCenter !== (user.costCenter ?? '');

  // B2c-T2 — does the picked engagement have a scheduled end? The served
  // option answers; the form used to compare the key against a UI-only key.
  //
  // Review B2cT2-BF-08 — only once the server HAS answered. While the options
  // are absent the rule is unknown, and "unknown" must not be drawn as
  // "optional": `endDateRuleKnown` is what the label and the warning read.
  const endDateRuleKnown = fieldOptions.isAvailable;
  const endDateRequired =
    endDateRuleKnown && fieldOptions.requiresEndDate(employmentType);

  // Status is NOT a field of this dialog (B2a-T2): it is not editable here, so
  // it cannot be dirty here. It used to contribute `statusDirty` to
  // `hasChanges`, which enabled Save for a change the dialog never sent.
  const hasChanges = profileDirty || identityDirty || employmentDirty;

  const handleSave = async () => {
    if (!canSave || isPending) return;
    setServerError(null);
    setEmailError(null);

    // Closes #250 round 8: re-compute the date-dirty flags from the
    // LIVE DOM values at click time. React state can lag the DOM if
    // the input lost focus + click happened in the same micro-task
    // (the blur-driven setState was scheduled but not yet flushed).
    // The render-time `employmentDirty` we computed above may say
    // "no date change" while the DOM clearly shows a typed value.
    // Reading refs at handleSave is the authoritative path.
    const submitStartDate =
      startDateRef.current?.value ?? startDate;
    const submitEndDate =
      endDateRef.current?.value ?? endDate;
    const dateDirty =
      isDateOnlyDirty(user.startDate, submitStartDate) ||
      isDateOnlyDirty(user.endDate, submitEndDate);
    const employmentDirtyAtClick = dateDirty || employmentDirty;

    let profileSucceeded = !(profileDirty || employmentDirtyAtClick);

    // 1. Profile + employment fields → PATCH /users/:id
    //    The server's PATCH body schema accepts both sets, so a single
    //    call covers profile + employment changes atomically.
    if (profileDirty || employmentDirtyAtClick) {
      const body: UpdateUserBody = {};
      if (firstName !== user.firstName) body.firstName = firstName;
      if (lastName !== user.lastName) body.lastName = lastName;
      if (phoneNumber !== (user.phoneNumber ?? '')) {
        body.phoneNumber = phoneNumber || null;
      }
      // Self-edit cannot change email per the server policy; the UI
      // already disables the field. Don't send it for self-edits.
      if (!emailDisabled && email !== user.email) {
        body.email = email;
      }
      // Role is dispatched only if the field is enabled (admin + cross-user)
      // and the snapped value differs.
      if (!roleDisabled) {
        const snapped = snapRoleForApi(roleId);
        const prevSnapped = snapRoleForApi(user.roles[0]?.roleId ?? 'member');
        if (snapped !== prevSnapped) body.role = snapped;
      }

      // ── Employment fields (bA1) ─────────────────────────────────────
      // Send each field only when it has changed vs. the loaded user.
      // Sending null clears the column server-side; sending the same
      // value short-circuits via the server's no-op detection.
      //
      // Closes #74: defensive UUID guard on departmentId / managerId.
      // Mock-fallback IDs (e.g. `dept-engineering`, `user-sarah-chen`)
      // can leak in when the tenant has no real departments configured
      // yet; the server rejects them with 422 VALIDATION_ERROR which
      // ends up aborting the PATCH transaction before position lands.
      // The fix: send null when the dirty value is non-UUID so the
      // position update still commits.
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (position !== user.position) {
        body.position = position.trim() === '' ? null : position;
      }
      // B2b-T3 review B2bT3-RV-02 / BF-B2bT3-04 — THE SCOPE THE ADMIN PICKED IS
      // SENT, WHOLE. It used to drop non-UUID ids and then omit the key when
      // none survived, and an omitted key on the server is decisions.md C7's
      // ALL-DEPARTMENTS person: a narrowing the admin asked for reached the
      // store as the widest scope on the rail, with the optimistic row still
      // showing the department names they picked. An id the server will not
      // take is a 422 the operator SEES (principles.md #4: nothing fails open).
      if (!sameScope(departmentScope, scopeFromUserDepartments(user.departments))) {
        body.departments = departmentScope.all
          ? { all: true, ids: [] }
          : { all: false, ids: [...departmentScope.ids] };
      }
      const newManagerId = managerId === 'none' ? null : managerId;
      if (newManagerId !== (user.managerId ?? null)) {
        body.managerId = newManagerId && UUID_RE.test(newManagerId) ? newManagerId : null;
      }
      if (employmentType !== user.employmentType) {
        body.employmentType = employmentType;
      }
      // Closes #216 + #250 round 8: Start Date / End Date use the
      // ref-read submit values computed above as canonical truth.
      // (See the rationale block at the top of handleSave for the
      // full root-cause write-up.)
      if (isDateOnlyDirty(user.startDate, submitStartDate)) {
        body.startDate = submitStartDate === '' ? null : submitStartDate;
      }
      if (isDateOnlyDirty(user.endDate, submitEndDate)) {
        body.endDate = submitEndDate === '' ? null : submitEndDate;
      }
      const newCostCenter = costCenter.trim() === '' ? null : costCenter;
      if (newCostCenter !== (user.costCenter ?? null)) {
        body.costCenter = newCostCenter;
      }

      // Empty body — possible when only RBAC-disabled fields are dirty.
      // Skip the call entirely (no-op behavior matches server semantic).
      if (Object.keys(body).length === 0) {
        profileSucceeded = true;
      } else {
        try {
          await updateUser.mutateAsync({ userId: user.id, body });
          profileSucceeded = true;
        } catch (err) {
          const { code, message } = parseErr(err);
          setServerError({ code, message });
          switch (code) {
            case 'EMAIL_EXISTS':
              setEmailError(message);
              toast.error('Email already in use', { description: message });
              return;
            case 'VALIDATION_ERROR':
              toast.error('Validation failed', { description: message });
              return;
            case 'FORBIDDEN':
              toast.error('Permission denied', { description: message });
              return;
            case 'INVALID_DEPARTMENT':
              toast.error('Department not found', { description: message });
              return;
            case 'INVALID_MANAGER':
              toast.error('Manager not found', { description: message });
              return;
            case 'EMPLOYEE_ID_EXISTS':
              toast.error('Employee ID already in use', { description: message });
              return;
            default:
              toast.error('Could not save profile', { description: message });
              return;
          }
        }
      }
    }

    // 2. Identity fields → PATCH /users/:id/identity-fields (F61)
    //    (No-op in v1 — identityDirty is always false; placeholder for
    //    future identity-fields editing inside this dialog.)
    if (identityDirty) {
      const identityBody: UpdateIdentityFieldsBody = {};
      try {
        await updateIdentity.mutateAsync({ userId: user.id, body: identityBody });
      } catch (err) {
        const { code, message } = parseErr(err);
        if (profileSucceeded) {
          // Partial save — profile committed, identity didn't.
          toast.warning('Saved profile but identity update failed', {
            description: message,
          });
        } else {
          toast.error('Identity update failed', { description: message });
        }
        setServerError({ code, message });
        return;
      }
    }

    // Closes Users-bug#1: the Save button is intentionally NOT gated on
    // hasChanges (see the footer comment re: #250 round 8), so the
    // operator can click Save with nothing actually dirty. In that case
    // no PATCH was issued above — don't fake a "User updated" success +
    // onSaved refresh for a true no-op; tell the operator honestly.
    if (!profileDirty && !employmentDirtyAtClick && !identityDirty) {
      toast.info('No changes to save');
      onOpenChange(false);
      return;
    }

    toast.success('User updated');
    onSaved({
      ...user,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`.trim(),
      phoneNumber: phoneNumber || null,
      email: emailDisabled ? user.email : email,
      updatedAt: new Date().toISOString(),
      // Mock-only state (kept in component memory for visual continuity
      // until users.department_id / position / manager_id columns ship —
      // CM-2 schema gap).
      position,
      // B2b-T3 — the optimistic value is the SET the operator just picked,
      // named from the same live list the picker rendered. TanStack Query
      // overwrites it with the server's answer moments later.
      departments: {
        all: departmentScope.all,
        items: departmentScope.all
          ? []
          : departmentScope.ids.map((id) => ({
              id,
              name: liveDepartments.find((d) => d.id === id)?.name ?? id,
            })),
      },
      managerId: managerId === 'none' ? null : managerId,
      managerName:
        managerId === 'none'
          ? null
          : (managerOptions.find((u) => u.id === managerId)?.fullName ?? null),
      employmentType,
      // Keep the optimistic mock-shape values as canonical YYYY-MM-DD
      // strings so the detail page's formatDateOnly helper renders the
      // same date the operator just typed (TanStack Query refetch will
      // overwrite with the API's authoritative value shortly).
      // Closes #250: explicit empty-string → null mapping so an empty
      // End Date is preserved as null in the optimistic update too,
      // and the loaded `user.startDate` always passes through
      // `normalizeDateOnly` so an ISO timestamp doesn't leak.
      startDate: startDate || (normalizeDateOnly(user.startDate) ?? null),
      endDate: endDate || null,
      costCenter: costCenter || null,
      // Unchanged by this dialog — status moves only through the lifecycle
      // transition path. It used to carry the local, never-sent selector
      // value, so an optimistic update could show a status the server had
      // not been asked for.
      status: user.status,
    });
    onOpenChange(false);
  };

  // Live tenant pickers — admin/owner/power_user, active, excluding the
  // user being edited. Replaces the mock USERS / DEPARTMENTS arrays so
  // the dialog shows the same data the Departments tab and Users list
  // show.
  //
  // Closes #135: prior filter only matched the legacy `r.roleId` after
  // stripping the 'role-' prefix; the deployed picker rendered empty
  // because the API now returns `role` as the canonical lean enum, not
  // the prefixed roleId. We match `roleName` (which is `api.role` from
  // the adapter) directly AND keep the prefix-stripping fallback so
  // future format drifts don't break the picker.
  // B2b-T3 — EVERY department, paged to the end (review BF-B2bT3-08): this
  // picker writes the person's scope, so a department it cannot show is one an
  // admin cannot choose — and a person already in a department past the API's
  // 200-row maximum would have their scope edited from a list that could not
  // name it.
  const { departments: liveDepartments } = useAllDepartments();
  const { users: tenantUsers } = useUsers({ pageSize: 200 });
  const isAdminEligibleManager = (u: User): boolean => {
    const apiRole = (u.roles[0]?.roleName ?? '').toLowerCase();
    if (apiRole === 'admin' || apiRole === 'owner' || apiRole === 'power_user') {
      return true;
    }
    return u.roles.some((r) => {
      // Strip the legacy `role-` and retired `system:` prefixes: the adapter
      // emits bare keys (B1-T4), and these two shapes are only tolerated.
      const stripped = (r.roleId ?? '')
        .replace(/^role-/, '')
        .replace(/^system:/, '')
        .toLowerCase();
      return stripped === 'admin' || stripped === 'owner' || stripped === 'power_user';
    });
  };
  const managerOptions = tenantUsers.filter(
    (u) => u.id !== user.id && u.status === 'active' && isAdminEligibleManager(u)
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !isPending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" />
            Edit user
          </DialogTitle>
          <DialogDescription>
            Update {user.fullName}'s profile, employment, and status.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-6 py-5 space-y-5">
            {/* Identity */}
            <section>
              <h4 className="text-sm font-semibold mb-1 flex items-center gap-1.5">
                <UserIcon className="h-3.5 w-3.5 text-muted-foreground" />
                Identity
              </h4>
              <p className="text-[11px] text-muted-foreground mb-3">
                Email is the user's login identifier and is read-only here.
                Changing it requires a dedicated identity-change flow with
                re-verification.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" required>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="h-9 text-xs"
                    disabled={isPending}
                  />
                </Field>
                <Field label="Last name" required>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="h-9 text-xs"
                    disabled={isPending}
                  />
                </Field>
                <Field label="Email (read-only)" required>
                  <Input
                    value={email}
                    type="email"
                    disabled
                    className="h-9 text-xs"
                    title="Email is the login identifier — change requires a dedicated identity flow"
                  />
                  {emailError && (
                    <p className="text-[10px] text-red-500 mt-1">{emailError}</p>
                  )}
                </Field>
                <Field label="Employee ID (read-only)">
                  <Input value={user.employeeId} disabled className="h-9 text-xs text-muted-foreground" />
                </Field>
                <Field label="Phone number">
                  <Input
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="h-9 text-xs"
                    placeholder="+1 555-0000"
                    disabled={isPending}
                  />
                </Field>
                <Field label="Cost center">
                  <Input value={costCenter} onChange={(e) => setCostCenter(e.target.value)} className="h-9 text-xs" placeholder="CC-000" />
                </Field>
                <Field label="Role">
                  <Select
                    value={roleId}
                    onValueChange={setRoleId}
                    disabled={roleDisabled || isPending}
                  >
                    <SelectTrigger
                      className="h-9 text-xs"
                      title={
                        isSelf
                          ? "You can't change your own role"
                          : !canAssignRole
                            ? 'You need role:assign:users to change roles'
                            : undefined
                      }
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/*
                       * B1-T4: the option value is the role KEY, which is what
                       * the adapter now writes to `user.roles[0].roleId` and
                       * what the API's role enum takes — so no-change
                       * detection still compares like with like, without the
                       * `system:<role>` sentinel the API used to synthesise.
                       * The three options are the ones `PATCH /users/:id`
                       * accepts; owner and power_user are changed elsewhere.
                       */}
                      <SelectItem value="viewer" className="text-xs">Viewer</SelectItem>
                      <SelectItem value="member" className="text-xs">Member</SelectItem>
                      <SelectItem value="admin" className="text-xs">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </section>

            <Separator />

            {/* Employment */}
            <section>
              <h4 className="text-sm font-semibold mb-1 flex items-center gap-1.5">
                <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />
                Employment
              </h4>
              <p className="text-[11px] text-muted-foreground mb-3">
                Position, department, and reporting line.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Position / duty title">
                  <Input value={position} onChange={(e) => setPosition(e.target.value)} className="h-9 text-xs" />
                </Field>
                {/*
                  B2c-T2 — the options are the server's answer
                  (GET /users/field-options). They were four hand-written
                  SelectItems whose values the submit path then translated
                  into something else.
                */}
                <Field label="Employment type">
                  {/* Review B2cT2-BF-03 — a blank picker now says why it is
                      blank, in the same words the create wizard uses. */}
                  {!fieldOptions.isAvailable ? (
                    <FieldOptionsNotice
                      isAvailable={fieldOptions.isAvailable}
                      isLoading={fieldOptionsLoading}
                      error={fieldOptionsError}
                      onRetry={refetchFieldOptions}
                    />
                  ) : (
                    <Select value={employmentType ?? ''} onValueChange={(v) => setEmploymentType(v as EmploymentType)}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {fieldOptions.employmentTypes.map((t) => (
                          <SelectItem key={t.value} value={t.value} className="text-xs">
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                {/* B2b-T3 — the SAME Department field, in the same place in
                    the same grid, editing a list instead of one value. */}
                <Field label="Department">
                  <DepartmentScopePicker
                    value={departmentScope}
                    onChange={setDepartmentScope}
                    departments={liveDepartments}
                    aria-label="Departments"
                  />
                </Field>
                <Field label="Reports to (manager)">
                  <Select value={managerId} onValueChange={setManagerId}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" className="text-xs">— No manager —</SelectItem>
                      {managerOptions.length === 0 ? (
                        <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                          No eligible managers in this tenant yet.
                        </div>
                      ) : (
                        managerOptions.map((u) => (
                          <SelectItem key={u.id} value={u.id} className="text-xs">
                            {u.fullName || u.email}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </Field>
                {/*
                 * Closes #216 + #250 round 8: DateInput wraps
                 * `<input type="date">` with an onBlur fallback that
                 * re-syncs state from the DOM value when React's
                 * native onChange misses an update. The ref hands the
                 * live DOM value to handleSave so the body reflects
                 * the operator's typed value even when state has not
                 * yet caught up. See date-input.tsx.
                 */}
                <Field label="Start date">
                  <DateInput
                    ref={startDateRef}
                    value={startDate}
                    onChange={setStartDate}
                    className="h-9 text-xs"
                    disabled={isPending}
                  />
                </Field>
                {/*
                  B2c-T2 — the served option says whether this engagement has
                  a scheduled end; the three `!== 'fte'` compares are gone.
                */}
                <Field
                  label={`End date${
                    !endDateRuleKnown ? '' : endDateRequired ? '' : ' (optional)'
                  }`}
                  required={endDateRequired}
                >
                  <DateInput
                    ref={endDateRef}
                    value={endDate}
                    onChange={setEndDate}
                    min={startDate || undefined}
                    className="h-9 text-xs"
                    disabled={isPending}
                  />
                  {endDateRequired && !endDate && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                      Required for a time-bounded engagement. The account
                      auto-deactivates on this date.
                    </p>
                  )}
                </Field>
              </div>
            </section>

            <Separator />

            {/* ── Account status, as a READ-ONLY fact ───────────────────── */}
            {/* B2a-T2. What stood here was a disabled <Select> whose label  */}
            {/* and helper text both admitted it persisted nothing — a       */}
            {/* control that looked like a control, could be driven, and     */}
            {/* threw the result away. It also fed `statusDirty` into         */}
            {/* `hasChanges`, so touching it enabled Save for an edit that    */}
            {/* was never sent.                                             */}
            {/* Lifecycle changes have ONE path and it is the transition     */}
            {/* table the user detail page renders from the server.         */}
            <section>
              <h4 className="text-sm font-semibold mb-1 flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                Account status
              </h4>
              <p className="text-[11px] text-muted-foreground mb-3">
                Status changes through the lifecycle actions on the user detail
                page, which offer exactly the transitions this account and your
                permissions allow.
              </p>
              <Field label="Status">
                <div className="h-9 flex items-center">
                  <UserStatusBadge status={user.status} />
                </div>
              </Field>
            </section>

            {serverError && (
              <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-700 dark:text-red-400">
                <p className="font-semibold">{serverError.code}</p>
                <p>{serverError.message}</p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <div className="flex items-center justify-between w-full">
            <p className="text-[11px] text-muted-foreground">
              {hasChanges ? 'Unsaved changes' : 'No changes'}
              {user.updatedAt && (
                <> · Last updated {new Date(user.updatedAt).toLocaleString()}</>
              )}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                Cancel
              </Button>
              {/*
                * Closes #250 round 8: Save button is no longer gated
                * on `!hasChanges`. Disabling the button on stale
                * `hasChanges` was the user-facing manifestation of
                * the controlled-input bug — when React state lagged
                * the DOM, the button stayed disabled and the click
                * was suppressed before handleSave could re-read the
                * DOM via refs. Keeping the button active when fields
                * are valid lets handleSave run, re-compute dirty
                * from refs, and either persist or no-op gracefully.
                * The "Unsaved changes" / "No changes" indicator above
                * still gives operators the dirty-state hint.
                */}
              <Button
                onClick={handleSave}
                disabled={!canSave || isPending}
              >
                <Save className="h-4 w-4 mr-1.5" />
                {isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function Field({
  label, required, children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
